import { ActionType as AccessTokenActionType, Action as AccessTokenAction } from '~/AccessToken';
import { LtMaxPrice, Update as ApiUpdate } from '~/Api/User/ProductGroup/LtMaxPrice';

import { Action, ActionType } from './Action';

export interface LtMaxPriceState {
	ltMaxPrice: LtMaxPrice | null;
	isFetching: boolean;
	request: Promise<any>;
}

type UpdateState =
	| {
			name: 'scheduled';
			timeoutId: NodeJS.Timeout;
			data: ApiUpdate;
	  }
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface Update {
	groupId: string;
	state: UpdateState;
}

export interface State {
	byId: { [key in string]: LtMaxPriceState };
	update: Update | null;
}

export const createState = (): State => ({
	byId: {},
	update: null,
});

// tslint:disable-next-line: cyclomatic-complexity
export const reducer = (state: State = createState(), action: Action | AccessTokenAction): State => {
	switch (action.type) {
		case ActionType.request:
			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						ltMaxPrice: state.byId[action.groupId]?.ltMaxPrice ?? null,
						isFetching: true,
						request: action.request,
					},
				},
			};

		case ActionType.receive: {
			const ltMaxPrice = state.byId[action.groupId];
			if (ltMaxPrice?.request !== action.request) {
				return state;
			}

			if (action.ltMaxPrice === null) {
				const byId = { ...state.byId };
				delete byId[action.groupId];
				return { ...state, byId };
			}

			return {
				...state,
				byId: {
					[action.groupId]: {
						...ltMaxPrice,
						ltMaxPrice: action.ltMaxPrice,
						isFetching: false,
					},
				},
			};
		}

		case ActionType.prune: {
			const byId = { ...state.byId };
			delete byId[action.groupId];
			return { ...state, byId };
		}

		case ActionType.scheduleUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'scheduled') {
				clearTimeout(updateState.timeoutId);
			}

			return {
				...state,
				update: {
					groupId: action.groupId,
					state: {
						name: 'scheduled',
						timeoutId: action.timeoutId,
						data: action.update,
					},
				},
			};
		}

		case ActionType.requestUpdate: {
			return {
				...state,
				update: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};
		}

		case ActionType.receiveUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'fetching' && updateState.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			if (action.ltMaxPrice !== null && action.groupId in byId) {
				byId[action.groupId] = {
					...byId[action.groupId],
					ltMaxPrice: action.ltMaxPrice,
				};
			}

			return {
				...state,
				byId,
				update: {
					groupId: action.groupId,
					state: action.ltMaxPrice !== null ? { name: 'updated' } : { name: 'failed' },
				},
			};
		}

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
};

export const selectLtMaxPrice = (state: State, groupId: string): LtMaxPriceState | null => state.byId[groupId];
